import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { TID } from "@/lib/testIds";
import Confetti from "@/components/Confetti";
import { useEntitlements } from "@/lib/useEntitlements";
import { hydrateFromCatalog, starterScansPhrase, planNumbers } from "@/lib/planCopy";
import {
  Zap,
  Info,
  Check,
  Loader2,
  CreditCard,
  RotateCcw,
  ArrowUpRight,
  ArrowDownRight,
  Gift,
} from "lucide-react";

const FEATURE_ROWS = [
  { key: "scans_per_month", label: "Scans / month", fmt: (v) => (v == null ? "Unlimited" : v.toLocaleString()) },
  { key: "brands", label: "Brands", fmt: (v) => (v == null ? "Unlimited" : v) },
  { key: "done_for_you", label: "Done-for-you scans", fmt: (v) => (v ? "Included" : "—") },
  { key: "clients_cap", label: "Client accounts", fmt: (v) => (v ? `Up to ${v}` : "—") },
];

// Same catalog is server-authoritative — we only tag which sub is highlighted.
const HIGHLIGHT_SUB = "starter"; // Starter is the only paid plan

export default function Billing() {
  const { workspace, refreshWorkspace } = useAuth();
  const { loaded: entLoaded, unknown: entUnknown, isPaid, scanUsed } = useEntitlements();
  const [catalog, setCatalog] = useState(null);
  const [history, setHistory] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [busySku, setBusySku] = useState(null);
  const [celebrate, setCelebrate] = useState(false);

  const [currency, setCurrency] = useState("INR"); // INR | USD

  const activeSubKey = workspace?.subscription_key;

  useEffect(() => {
    (async () => {
      try {
        const p = await api.get("/billing/plans");
        setCatalog(p.data);
        // Push the real catalogue into planCopy so every "15 scans a month" in
        // the app follows the server instead of a frontend literal.
        hydrateFromCatalog(p.data);
      } catch {
        /* keep null */
      }
      try {
        const t = await api.get("/payments/history");
        setHistory(t.data?.items || []);
      } catch {
        /* ignore */
      }
      try {
        const l = await api.get("/workspaces/current/credit-ledger?limit=10");
        setLedger(l.data?.items || []);
      } catch {
        /* ignore */
      }
    })();
  }, [workspace?.plan, workspace?.subscription_key]);

  const usage = workspace?.usage_today;
  const quotaModel = usage?.quota_model || "daily";
  const isMonthly = quotaModel === "monthly";

  // Monthly-allowance shape (paid plans)
  const allowanceBalance = Number(usage?.allowance_balance ?? 0);
  const allowanceCap = Number(usage?.allowance_cap ?? 0);
  const allowanceUsed = Math.max(0, allowanceCap - allowanceBalance);
  const burstUsed = Number(usage?.daily_burst_used ?? 0);
  const burstLimit = usage?.daily_burst_limit ?? null;
  const burstRemaining = burstLimit != null ? Math.max(0, burstLimit - burstUsed) : null;
  const burstHit = burstLimit != null && burstUsed >= burstLimit && allowanceBalance > 0;
  const periodEnd = usage?.allowance_period_end;
  const resetLabel = periodEnd
    ? new Date(periodEnd).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : null;

  // Daily-model shape (free / grandfathered edge cases)
  const dailyUsed = usage?.account_used ?? 0;
  // No invented default. `?? 10` stated a daily limit of 10 that no plan in the
  // catalogue grants, on the one page where users check what they're paying for.
  const dailyLimit = Number.isFinite(usage?.account_limit) ? usage.account_limit : null;
  const dailyKnown = dailyLimit !== null;
  const dailyRemaining = dailyKnown ? Math.max(0, dailyLimit - dailyUsed) : null;

  // Unified header pill inputs — monthly for paid, daily for free.
  const headerUsed = isMonthly ? allowanceUsed : dailyUsed;
  const headerLimit = isMonthly ? allowanceCap : dailyLimit;
  const headerRemaining = isMonthly ? allowanceBalance : dailyRemaining;
  const pct = Number.isFinite(headerLimit) && headerLimit > 0
    ? Math.min(100, Math.round((headerUsed / headerLimit) * 100))
    : 0;

  const subs = useMemo(() => {
    if (!catalog?.subscriptions) return [];
    return catalog.subscriptions.filter((s) => s.cadence === "monthly");
  }, [catalog]);

  const priceLabel = (paise, usd) => {
    if (currency === "USD") return usd != null ? `$${usd.toLocaleString()}` : "—";
    return `₹${(paise / 100).toLocaleString("en-IN")}`;
  };

  const startSubscribe = async (planKey) => {
    setBusySku(planKey);
    // USD → Stripe hosted checkout (redirect). INR → Razorpay modal.
    if (currency === "USD") {
      try {
        const resp = await api.post("/payments/stripe/subscribe", {
          plan_key: planKey,
          origin_url: window.location.origin,
        });
        const url = resp.data?.url;
        if (!url) throw new Error("No checkout URL returned");
        window.location.href = url; // navigate to Stripe Checkout
        return; // page is leaving — keep busy state until redirect
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not start Stripe checkout.");
        setBusySku(null);
        return;
      }
    }
    if (!window.Razorpay) {
      toast.error("Razorpay checkout is still loading — try again in a moment.");
      setBusySku(null);
      return;
    }
    try {
      const resp = await api.post("/payments/razorpay/subscribe", { plan_key: planKey });
      const opts = resp.data;
      const rzp = new window.Razorpay({
        key: opts.razorpay_key_id,
        subscription_id: opts.subscription_id,
        name: `Leadmato — ${opts.plan_name}`,
        description: `${opts.cadence === "annual" ? "Annual" : "Monthly"} subscription`,
        theme: { color: "#0F5535" },
        modal: { ondismiss: () => setBusySku(null) },
        handler: async (rzpResp) => {
          try {
            await api.post("/payments/razorpay/subscription/verify", {
              razorpay_subscription_id: rzpResp.razorpay_subscription_id,
              razorpay_payment_id: rzpResp.razorpay_payment_id,
              razorpay_signature: rzpResp.razorpay_signature,
            });
            setCelebrate(true);
            toast.success(`🎉 Congratulations! You're on ${opts.plan_name} — everything's unlocked.`);
            await refreshWorkspace();
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Subscription verification failed.");
          } finally {
            setBusySku(null);
          }
        },
      });
      rzp.on("payment.failed", (r) => {
        toast.error(r?.error?.description || "Payment failed.");
        setBusySku(null);
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start subscription.");
      setBusySku(null);
    }
  };

  if (!catalog) {
    return (
      <div className="flex items-center gap-2 text-lm-text2 py-10">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading billing…
      </div>
    );
  }

  return (
    <div>
      {celebrate && <Confetti />}
      {/* Header */}
      <div className="mb-8">
        <div className="text-xs font-mono uppercase tracking-widest text-lm-secondary mb-1.5">Billing</div>
        <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight">
          Plan &amp; usage
        </h1>
      </div>

      {/* Current status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
        <div className="md:col-span-1 rounded-xl border border-lm-border bg-lm-tint/50 p-6 relative overflow-hidden" data-testid="billing-usage-card">
          {!entLoaded ? (
            /* Pending. This card is a billing statement — it says what you've
               used and what you still have. Guessing here prints a number that
               isn't true, so it prints "--" until we actually know. */
            <div
              data-testid={entUnknown ? "billing-usage-unknown" : "billing-usage-pending"}
              aria-busy={entUnknown ? "false" : "true"}
            >
              <div className="text-xs font-mono uppercase tracking-widest text-lm-text3 mb-2">
                Your usage
              </div>
              <div className="text-5xl font-display font-semibold text-lm-text3/50 font-mono">--</div>
              {/* "Loading your plan…" is true while a fetch is in flight and a
                  lie the moment the ladder stops. This is a BILLING surface —
                  the worst possible place to leave a confident sentence that
                  has quietly stopped being true. Once we've stopped, the slot
                  says nothing and the banner above carries the explanation. */}
              {!entUnknown && (
                <div className="mt-3 text-xs text-lm-text2 font-mono">Loading your plan…</div>
              )}
              <Zap className="absolute top-4 right-4 w-8 h-8 text-lm-text3/10" />
            </div>
          ) : isPaid ? (
            <>
              <div className="text-xs font-mono uppercase tracking-widest text-lm-text3 mb-2">
                Scans this month
              </div>
              <div className="text-5xl font-display font-semibold text-lm-primary font-mono" data-testid="usage-primary-figure">
                {allowanceUsed}
                <span className="text-2xl text-lm-text3">/{allowanceCap}</span>
              </div>
              <div className="mt-3">
                <div className="w-full h-1.5 bg-lm-border rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${pct >= 80 ? "bg-lm-secondary" : "bg-lm-primary"}`} style={{ width: `${pct}%` }} />
                </div>
                <div className="mt-2 text-xs text-lm-text2 font-mono">
                  {allowanceBalance} of {allowanceCap} scans this month{resetLabel && <> · resets {resetLabel}</>}
                </div>
                {burstHit && (
                  <div className="mt-2 text-xs font-mono text-amber-700 flex items-start gap-1.5" data-testid="burst-hit-notice">
                    <span>⚠</span>
                    <span>Daily fair-use limit reached — resets tomorrow. {allowanceBalance} monthly scan{allowanceBalance === 1 ? "" : "s"} still waiting.</span>
                  </div>
                )}
              </div>
              <Zap className="absolute top-4 right-4 w-8 h-8 text-lm-primary/20" />
            </>
          ) : (
            <>
              <div className="text-xs font-mono uppercase tracking-widest text-lm-text3 mb-3">First scan free</div>
              <div className="space-y-2">
                {/* Brand import is NOT a one-time allowance and we get no
                    used/available signal for it — so this row states the one
                    thing that is actually true (it's free, capped at one every
                    30 minutes) instead of showing a ✓/1 counter it can't back up. */}
                <div className="flex items-center gap-2 text-sm" data-testid="billing-brand-import-row">
                  <span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 bg-lm-primary">
                    <span className="w-1.5 h-1.5 rounded-full bg-white" />
                  </span>
                  <span className="text-lm-text">
                    Brand import <span className="text-lm-text3">— free, one every 30 minutes</span>
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-white shrink-0 ${scanUsed ? "bg-lm-text3" : "bg-lm-primary"}`}>{scanUsed ? "✓" : "1"}</span>
                  <span className="text-lm-text">Find-Customers scan {scanUsed ? <span className="text-lm-text3">— used</span> : <span className="text-lm-text3">— available</span>}</span>
                </div>
              </div>
              <p className="mt-4 text-xs text-lm-text2 leading-relaxed">
                Upgrade to <strong>Starter</strong> for {starterScansPhrase()}, plus open-source links and saving leads.
              </p>
              <Zap className="absolute top-4 right-4 w-8 h-8 text-lm-primary/20" />
            </>
          )}
        </div>
        <div className="md:col-span-2 rounded-xl border border-lm-tint2 bg-lm-tint2/30 p-6 flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shrink-0">
            <Info className="w-5 h-5 text-lm-primary" />
          </div>
          <div>
            <h3 className="text-lg font-display font-semibold mb-1">
              {entLoaded && isPaid ? "You're on Starter." : "How scans work"}
            </h3>
            <p className="text-sm text-lm-text2 leading-relaxed">
              {/* The plan-neutral half of this copy is true either way, so the
                  pending state keeps it and simply drops the upgrade pitch. */}
              {!entLoaded
                ? "Every scan runs on-demand. If it returns no usable signal we refund it — you only pay for real results."
                : isPaid
                  ? `Your Starter plan includes ${starterScansPhrase()}. If a scan finds nothing usable, we refund it automatically.`
                  : `Every scan runs on-demand. If it returns no usable signal we refund it — you only pay for real results. Upgrade to Starter for ${starterScansPhrase()}.`}
            </p>
          </div>
        </div>
      </div>

      {/* Compare plans header + toggles */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-display font-semibold">Your plan</h2>
          <p className="text-sm text-lm-text2 mt-1">
            One simple plan — {starterScansPhrase()}, up to {planNumbers().starterKeywords} keywords each.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <CurrencyToggle currency={currency} setCurrency={setCurrency} />
        </div>
      </div>

      {/* Subscriptions */}
      <div
        className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-4"
        data-testid="billing-subscriptions-grid"
      >
        {subs.map((p) => {
          const isActive = activeSubKey === p.key;
          const baseName = p.name.replace(" (Annual)", "");
          const highlight = baseName.toLowerCase() === HIGHLIGHT_SUB;
          return (
            <div
              key={p.key}
              data-testid={TID.billingSubCard}
              className={`rounded-xl border p-5 flex flex-col relative overflow-hidden ${
                highlight
                  ? "border-lm-primary bg-lm-surface shadow-[0_10px_28px_-10px_rgba(15,85,53,0.25)]"
                  : "border-lm-border bg-lm-surface"
              }`}
            >
              {highlight && (
                <div className="absolute top-3 right-3 text-[10px] font-mono uppercase tracking-widest bg-lm-primary text-white px-2 py-1 rounded">
                  Recommended
                </div>
              )}
              <div className="text-sm text-lm-text2">{baseName}</div>
              <div className="mt-1 flex items-baseline gap-1.5">
                <div className="text-3xl font-display font-semibold">
                  {priceLabel(p.price_paise, p.usd)}
                </div>
                <div className="text-xs text-lm-text3 font-mono">/mo</div>
              </div>

              <ul className="space-y-1.5 mt-4 mb-5 flex-1">
                {FEATURE_ROWS.map((f) => (
                  <li key={f.key} className="flex items-start gap-2 text-sm text-lm-text">
                    <Check
                      className="w-3.5 h-3.5 text-lm-primary mt-1 shrink-0"
                      strokeWidth={2.5}
                    />
                    <span className="text-lm-text2">{f.label}:</span>
                    <span className="font-medium">{f.fmt(p[f.key])}</span>
                  </li>
                ))}
              </ul>

              {isActive ? (
                <div
                  data-testid="billing-current-plan-chip"
                  className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium border border-lm-primary bg-lm-tint text-lm-primary"
                >
                  <Check className="w-4 h-4" /> Current plan
                </div>
              ) : (
                <button
                  data-testid={TID.billingSubscribeBtn}
                  onClick={() => startSubscribe(p.key)}
                  disabled={busySku !== null}
                  className={`w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 font-semibold disabled:opacity-60 ${
                    highlight
                      ? "bg-lm-primary text-white hover:bg-lm-primaryHover"
                      : "border border-lm-border bg-lm-surface hover:bg-lm-tint"
                  }`}
                >
                  {busySku === p.key ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4" />
                  )}
                  Subscribe
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Credit ledger */}
      <div className="mt-12 mb-8">
        <h2 className="text-2xl font-display font-semibold mb-1">Recent scan activity</h2>
        <p className="text-sm text-lm-text2 mb-4">
          Every scan, refund and grant — from your workspace&rsquo;s credit ledger. Last 10 events.
        </p>
        <div
          data-testid={TID.billingCreditLedger}
          className="bg-lm-surface border border-lm-border rounded-xl overflow-hidden"
        >
          {ledger.length === 0 ? (
            <div className="p-6 text-sm text-lm-text2">
              No credit activity yet. Run a scan or purchase a plan to see it here.
            </div>
          ) : (
            <ul className="divide-y divide-lm-border">
              {ledger.map((it, idx) => (
                <LedgerRow key={it.tx_id || idx} entry={it} />
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Payment history */}
      <div className="mb-10">
        <h2 className="text-2xl font-display font-semibold mb-1">Payment history</h2>
        <p className="text-sm text-lm-text2 mb-4">
          Your completed payments and refunds. Abandoned or incomplete checkouts aren&rsquo;t shown —
          they never take money.
        </p>
        <div className="bg-lm-surface border border-lm-border rounded-xl overflow-hidden">
          {history.length === 0 ? (
            <div className="p-6 text-sm text-lm-text2">No payments yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-lm-tint/40 border-b border-lm-border">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">When</th>
                  <th className="text-left px-4 py-3 font-medium">Product</th>
                  <th className="text-right px-4 py-3 font-medium">Amount</th>
                  <th className="text-right px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {history.map((p) => (
                  <tr
                    key={p.payment_id}
                    data-testid={TID.billingTransactionsRow}
                    className="border-t border-lm-border/60 first:border-t-0"
                  >
                    <td className="px-4 py-3 text-lm-text2 font-mono text-xs">
                      {new Date(p.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 capitalize">
                      {p.sku
                        ? String(p.sku).replace(/_/g, " ")
                        : p.provider === "razorpay"
                        ? "Razorpay"
                        : p.provider}
                    </td>
                    <td className={`px-4 py-3 text-right font-mono ${p.status === "paid" ? "" : "text-lm-text3 line-through decoration-lm-text3/40"}`}>
                      {p.currency === "INR"
                        ? `₹${(p.amount / 100).toLocaleString("en-IN")}`
                        : `$${p.amount}`}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {(() => {
                        const meta = {
                          paid:     { label: "Paid",              cls: "bg-lm-tint2 text-lm-primary" },
                          failed:   { label: "Failed",            cls: "bg-red-50 text-red-600" },
                          expired:  { label: "Expired",           cls: "bg-lm-tint text-lm-text3" },
                          refunded: { label: "Refunded",          cls: "bg-lm-tint text-lm-text3" },
                          pending:  { label: "Incomplete checkout", cls: "bg-amber-50 text-amber-700" },
                        }[p.status] || { label: p.status, cls: "bg-lm-tint text-lm-text3" };
                        return (
                          <span
                            title={p.status === "pending" ? "Checkout wasn't completed — no money was taken." : undefined}
                            className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-mono font-semibold ${meta.cls}`}
                          >
                            {meta.label}
                          </span>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function CurrencyToggle({ currency, setCurrency }) {
  return (
    <div
      data-testid={TID.billingCurrencyToggle}
      className="inline-flex items-center rounded-full border border-lm-border bg-lm-surface p-0.5"
    >
      {["INR", "USD"].map((c) => (
        <button
          key={c}
          data-testid={`currency-${c}`}
          onClick={() => setCurrency(c)}
          className={`px-3 py-1.5 text-xs font-mono rounded-full transition-colors ${
            currency === c
              ? "bg-lm-primary text-white"
              : "text-lm-text2 hover:text-lm-text"
          }`}
        >
          {c}
        </button>
      ))}
    </div>
  );
}

const LEDGER_LABEL = {
  charge: "Scan used",
  refund: "Scan refunded",
  grant: "Scans granted",
};

const LEDGER_ICON = {
  charge: ArrowUpRight,
  refund: RotateCcw,
  grant: Gift,
};

function LedgerRow({ entry }) {
  const kind = entry.kind || "charge";
  const Icon = LEDGER_ICON[kind] || ArrowUpRight;
  const delta = Number(entry.delta || 0);
  const label = LEDGER_LABEL[kind] || (entry.reason || "").replace(/_/g, " ");
  const reason = (entry.reason || "").replace(/^refund_/, "").replace(/_/g, " ");
  const positive = delta > 0;
  return (
    <li
      data-testid={TID.billingCreditLedgerRow}
      className="flex items-center gap-3 px-4 py-3 text-sm"
    >
      <span
        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
          kind === "charge"
            ? "bg-lm-tint text-lm-text2"
            : kind === "refund"
            ? "bg-emerald-50 text-emerald-700"
            : "bg-amber-50 text-amber-700"
        }`}
      >
        {kind === "charge" ? (
          <ArrowDownRight className="w-4 h-4" />
        ) : (
          <Icon className="w-4 h-4" />
        )}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-lm-text font-medium">{label}</div>
        <div className="text-xs text-lm-text3 capitalize truncate">
          {reason || "—"}
          {entry.ref_id ? (
            <span className="ml-1.5 font-mono text-lm-text3/70">· {entry.ref_id}</span>
          ) : null}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div
          className={`text-sm font-mono font-semibold ${
            positive ? "text-emerald-700" : "text-lm-text"
          }`}
        >
          {positive ? "+" : ""}
          {delta}
        </div>
        <div className="text-[11px] text-lm-text3 font-mono">
          bal {entry.balance_after ?? "—"}
        </div>
      </div>
      <div className="text-[11px] text-lm-text3 font-mono w-24 text-right shrink-0 hidden md:block">
        {entry.created_at ? new Date(entry.created_at).toLocaleString() : ""}
      </div>
    </li>
  );
}
